import AiStarIcon from "@redotech/redo-icons/ai-star-orange.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { aiSubjectLineTones } from "@redotech/redo-model/ai-subject-line-tones";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as styles from "./ai-suggestions.module.css";
interface AiSubjectLineSuggestionsProps {
  subject: string;
  onSuggestionClick: (suggestion: string) => void;
  getAiSubjectLineSuggestions: (params: {
    text: string;
    tone?: string;
  }) => Promise<{ text: string; tone: string }[]>;
}

export const AiSubjectLineSuggestions = memo(function AiSubjectLineSuggestions(
  props: AiSubjectLineSuggestionsProps,
) {
  const { subject, onSuggestionClick, getAiSubjectLineSuggestions } = props;

  const [selectedTone, setSelectedTone] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<
    { text: string; tone: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [shouldShowPanel, setShouldShowPanel] = useState(false);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const toneButtonRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const previousSubjectRef = useRef<string>(subject);
  const suggestionSelectedRef = useRef<boolean>(false);
  const isInitialMount = useRef<boolean>(true);
  const hasUserEditedSubjectRef = useRef<boolean>(false);
  const isLoadingRef = useRef<boolean>(false);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const percentage = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setScrollPercentage(percentage);
    }
  };

  const scrollToSelectedTone = useCallback(() => {
    if (!selectedTone || !scrollContainerRef.current) return;

    const selectedButton = toneButtonRefs.current[selectedTone];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedTone]);

  const handleScrollbarDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    const scrollbar = e.currentTarget;
    const container = scrollContainerRef.current;
    if (!container) return;

    const startX = e.clientX;
    const thumbWidth = 38;
    const maxThumbLeft = scrollbar.clientWidth - thumbWidth;
    const startThumbLeft = scrollPercentage * maxThumbLeft;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newThumbLeft = Math.max(
        0,
        Math.min(maxThumbLeft, startThumbLeft + deltaX),
      );
      const newPercentage = newThumbLeft / maxThumbLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      container.scrollLeft = newPercentage * maxScroll;
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const loadSuggestions = useCallback(
    async (text: string, tone?: string) => {
      if (!getAiSubjectLineSuggestions || !text) {
        return;
      }

      // Prevent concurrent calls
      if (isLoadingRef.current) {
        return;
      }

      isLoadingRef.current = true;
      setIsLoading(true);
      setAiSuggestions([]);

      try {
        const suggestions = await getAiSubjectLineSuggestions({ text, tone });
        setAiSuggestions(suggestions);
        setIsLoading(false);
        isLoadingRef.current = false;
      } catch (error) {
        console.error("[AI Suggestions] Failed to load:", error);
        setShouldShowPanel(false);
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [getAiSubjectLineSuggestions],
  );

  // Scroll to selected tone when it changes or when panel becomes visible
  useEffect(() => {
    if (shouldShowPanel && !isLoading) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToSelectedTone();
      }, 100);
    }
  }, [selectedTone, shouldShowPanel, isLoading, scrollToSelectedTone]);

  useEffect(() => {
    // Handle initial mount - just set the flag and don't show anything
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousSubjectRef.current = subject;
      return;
    }

    // If a suggestion was just selected, don't show the panel but mark as edited for future
    if (suggestionSelectedRef.current) {
      suggestionSelectedRef.current = false;
      previousSubjectRef.current = subject;
      hasUserEditedSubjectRef.current = true; // Mark as edited so future edits will trigger suggestions
      return;
    }

    // If subject changed (user is typing/editing), mark as edited and handle state
    if (subject !== previousSubjectRef.current) {
      // Mark that the user has edited the subject line
      hasUserEditedSubjectRef.current = true;

      if (isDismissed) {
        setIsDismissed(false);
      }

      setAiSuggestions([]);
      // If panel was already showing, set loading state immediately
      if (shouldShowPanel) {
        setIsLoading(true);
      }
    }
    previousSubjectRef.current = subject;

    if (!subject || !getAiSubjectLineSuggestions) {
      setAiSuggestions([]);
      setShouldShowPanel(false);
      return;
    }

    // Only fetch and show suggestions if the user has edited the subject line
    if (!hasUserEditedSubjectRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setShouldShowPanel(true);
      void loadSuggestions(subject, selectedTone || undefined);
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, isDismissed, selectedTone]);

  const shouldShow = !isDismissed && subject.length > 0 && shouldShowPanel;

  if (!shouldShow) {
    return null;
  }

  const hasPaddingBottom = isLoading || aiSuggestions.length === 0;

  return (
    <div className={styles.container}>
      <Flex
        className={`${styles.suggestionsPanel} ${hasPaddingBottom ? styles.hasPaddingBottom : ""}`}
        dir="column"
        gap="sm"
        p="none"
      >
        <Flex dir="column" gap="lg" w="full">
          <Flex
            align="center"
            dir="row"
            justify="space-between"
            pl="md"
            pr="md"
            w="full"
          >
            <Flex align="center" gap="sm">
              <AiStarIcon
                className={`${styles.aiStarIcon} ${isLoading ? styles.spinning : ""}`}
              />
              <Text fontSize="xs" textColor="primary">
                {isLoading
                  ? "Generating suggestions..."
                  : "Subject line suggestions"}
              </Text>
            </Flex>
            <div
              className={styles.dismissButton}
              onClick={() => {
                setIsDismissed(true);
              }}
            >
              <XIcon height="14" width="14" />
            </div>
          </Flex>
          {!isLoading && (
            <>
              <div
                className={styles.tonesContainer}
                onScroll={handleScroll}
                ref={scrollContainerRef}
              >
                {aiSubjectLineTones.map((tone) => {
                  const isSelected = selectedTone === tone;
                  return (
                    <div
                      className={`${styles.tonePill} ${isSelected ? styles.selected : styles.unselected}`}
                      key={tone}
                      onClick={() => {
                        const newTone = selectedTone === tone ? null : tone;
                        setSelectedTone(newTone);
                        void loadSuggestions(subject, newTone || undefined);
                      }}
                      ref={(el) => {
                        toneButtonRefs.current[tone] = el;
                      }}
                    >
                      {tone}
                    </div>
                  );
                })}
              </div>
              <div
                className={styles.scrollbarTrack}
                onMouseDown={handleScrollbarDrag}
              >
                <div
                  className={styles.scrollbarThumb}
                  style={{
                    left: scrollContainerRef.current?.clientWidth
                      ? `${scrollPercentage * (100 - (40 / scrollContainerRef.current.clientWidth) * 100)}%`
                      : "0%",
                  }}
                />
              </div>
            </>
          )}
          {!isLoading && aiSuggestions.length > 0 && (
            <Flex dir="column" gap="md" w="full">
              {aiSuggestions.map((suggestion, index) => {
                const isLastItem = index === aiSuggestions.length - 1;
                return (
                  <div key={index}>
                    <Flex
                      align="center"
                      className={`${styles.suggestionItem} ${isLastItem ? styles.last : ""}`}
                      justify="space-between"
                      onClick={() => {
                        suggestionSelectedRef.current = true;
                        onSuggestionClick(suggestion.text);
                        setAiSuggestions([]);
                        setShouldShowPanel(false);
                      }}
                      w="full"
                    >
                      <Text fontSize="xs" px="md" py="xs" textColor="primary">
                        {suggestion.text}
                      </Text>
                    </Flex>
                  </div>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
});
